<script setup>
import { computed, ref } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
  sender,
} = useMessageContext();

/* Uhrzeit */
const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

/* Status-Logik wie im Original */
const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }

  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});

/* -------- Kundendaten / Custom Attributes (vom Sender) -------- */

const customAttributes = computed(() => {
  const s = sender.value || {};
  return s.custom_attributes || s.customAttributes || {};
});

/**
 * meta_enabled_from:
 * - Wenn gesetzt: nur Messages ab diesem Zeitpunkt zeigen Infos
 * - Wenn NICHT gesetzt: alles anzeigen (damit es nicht komplett leer ist)
 */
const metaEnabledFrom = computed(() => {
  const raw = customAttributes.value.meta_enabled_from;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
});

const createdAtDate = computed(() => {
  if (!createdAt.value) return null;
  const d = new Date(createdAt.value * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
});

const isMessageNewEnoughForMeta = computed(() => {
  // Wenn kein meta_enabled_from vorhanden ist → NICHT blocken
  if (!metaEnabledFrom.value || !createdAtDate.value) return true;
  return createdAtDate.value >= metaEnabledFrom.value;
});

/* URL */
const currentUrl = computed(() => {
  return (
    customAttributes.value.current_url ||
    customAttributes.value.currentUrl ||
    null
  );
});

/* andere Attribute */
const otherAttributes = computed(() => {
  const result = {};
  Object.entries(customAttributes.value || {}).forEach(([key, val]) => {
    if (
      key !== 'current_url' &&
      key !== 'currentUrl' &&
      key !== 'meta_enabled_from' &&
      val !== null &&
      val !== ''
    ) {
      result[key] = val;
    }
  });
  return result;
});

const hasOtherAttributes = computed(
  () => Object.keys(otherAttributes.value).length > 0
);

/* Sichtbarkeit: nur WebWidget, INCOMING, neue Messages und wenn Daten da sind */
const shouldShowCustomerMeta = computed(() => {
  return (
    isAWebWidgetInbox.value &&
    messageType.value === MESSAGE_TYPES.INCOMING &&
    isMessageNewEnoughForMeta.value &&
    (currentUrl.value || hasOtherAttributes.value)
  );
});

const showCustomerCurrentUrl = computed(
  () => shouldShowCustomerMeta.value && !!currentUrl.value
);

const showCustomerMoreToggle = computed(
  () => shouldShowCustomerMeta.value && hasOtherAttributes.value
);

const showMoreDetails = ref(false);
</script>

<template>
  <!-- Basis wie vorher: Zeit + Status, alles in text-xs -->
  <div class="flex flex-col gap-0.5 text-xs">
    <div class="flex items-center gap-1.5">
      <div class="inline">
        <time class="inline">{{ readableTime }}</time>
      </div>
      <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
      <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
    </div>

    <!-- URL: bewusst etwas kleiner als time -->
    <div
      v-if="showCustomerCurrentUrl"
      class="mt-0.5 text-[10px]"
    >
      <a
        :href="currentUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="underline text-n-blue-text break-all"
      >
        {{ currentUrl }}
      </a>
    </div>

    <!-- "Mehr über diesen Kunden" + Details, auch kleiner -->
    <div
      v-if="showCustomerMoreToggle"
      class="mt-0.5 text-[10px] text-n-slate-11"
    >
      <button
        type="button"
        class="underline"
        @click.stop="showMoreDetails = !showMoreDetails"
      >
        {{ showMoreDetails ? 'Weniger' : 'Mehr' }}
      </button>

      <div
        v-if="showMoreDetails"
        class="mt-0.5 space-y-0.5"
      >
        <div
          v-for="(val, key) in otherAttributes"
          :key="key"
        >
          {{ key }}: {{ val }}
        </div>
      </div>
    </div>
  </div>
</template>
